// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { GOOGLE_EVENTS } from "@sc/XDL/constants";
import { Draft } from "immer";

// Define the type for Google Analytics handler configuration
export type xGoogleAnalyticsHandlerType = {
    trackGoogleAnalytics?: boolean;
    // Basic Events
    checkoutFlowEvents?: boolean;
    addToCartEvent?: boolean;
    addToCartEventName?: string;
    purchaseEvent?: boolean;
    purchaseEventName?: string;
    addToWishListEvent?: boolean;
    addToWishListEventName?: string;
    removeFromCartLineEvent?: boolean;
    removeFromCartLineEventName?: string;
    pdpViewEvent?: boolean;
    pdpViewEventName?: string;
    productImpressionEvent?: boolean;
    productImpressionEventName?: string;
    selectItemEvent?: boolean;
    selectItemEventName?: string;
    viewCartEvent?: boolean;
    viewCartEventName?: string;
    loginEvent?: boolean;
    loginEventName?: string;
    loginIssueEvent?: boolean;
    loginIssueEventName?: string;
    searchEvent?: boolean;
    searchEventName?: string;
    contactUsEvent?: boolean;
    contactUsEventName?: string;
    plpFilterEvent?: boolean;
    plpFilterEventName?: string;
    plpSearchEvent?: boolean;
    plpSearchEventName?: string;
    pdpShareEvent?: boolean;
    pdpShareEventName?: string;
    requestAQuoteEvent?: boolean;
    requestAQuoteEventName?: string;
    myAcctQuickLinksEvent?: boolean;
    myAcctQuickLinksEventName?: string;
    myAcctFlyoutEvent?: boolean;
    myAcctFlyoutEventName?: string;
    navBarClickEvent?: boolean;
    navBarClickEventName?: string;
    toolBarNavEvent?: boolean;
    toolBarNavEventName?: string;
    homepageRibbonevent?: boolean;
    homepageRibbonEventName?: string;
    requestAccountEvent?: boolean;
    requestAccountEventName?: string;
    locationsEvent?: boolean;
    locationsEventName?: string;
    myacctMylistsEvent?: boolean;
    myacctMylistsEventName?: string;
    myacctQuotesEvent?: boolean;
    myacctQuotesEventName?: string;
    addListToCartEvent?: boolean;
    addListToCartEventName?: string;
    myacctListsMoreactionsEvent?: boolean;
    myacctListsMoreactionsEventName?: string;
    quickOrderSearchEvent?: boolean;
    quickOrderSearchEventName?: string;
    quickOrderUploadEvent?: boolean;
    quickOrderUploadEventName?: string;
    requestProductEvent?: boolean;
    requestProductEventName?: string;
    editCartEvent?: boolean;
    editCartEventName?: string;
    reorderEvent?: boolean;
    reorderEventName?: string;
    myacctOrderhistoryDaterangeEvent?: boolean;
    myacctOrderhistoryDaterangeEventName?: string;
    myacctOrderhistoryShiptoEvent?: boolean;
    myacctOrderhistoryShiptoEventName?: string;
    myacctOrderEvent?: boolean;
    myacctOrderEventName?: string;
    quoteAcceptedEvent?: boolean;
    quoteAcceptedEventName?: string;
    quoteDeclinedEvent?: boolean;
    quoteDeclinedEventName?: string;
    quoteProposedEvent?: boolean;
    quoteProposedEventName?: string;
    contactSalesEvent?: boolean;
    contactSalesEventName?: string;
    pdpImageClickEvent?: boolean;
    pdpImageClickEventName?: string;
    myacctAddUserEvent?: boolean;
    myacctAddUserEventName?: string;
    myacctEditUserEvent?: boolean;
    myacctEditUserEventName?: string;
    myacctViewUserEvent?: boolean;
    myacctViewUserEventName?: string;
    footerClicksEvent?: boolean;
    footerClicksEventName?: string;
    fileDownloadEvent?: boolean;
    fileDownloadEventName?: string;
    pdpWarehouseInventoryEvent?: boolean;
    pdpWarehouseInventoryEventName?: string;
    industriesServedEvent?: boolean;
    industriesServedEventName?: string;
    myacctRecentOrdersEvent?: boolean;
    myacctRecentOrdersEventName?: string;
    // Settings
    includeProductBrand?: boolean;
    includeProductCategory?: boolean;
    // Flags
    shouldTrackPayment?: boolean;
    shouldTrackBeginOnePageCheckout?: boolean;
    affiliation?: string;
    salesRep?: string;
    billToNum?: string;
    shipToNum?: string;
    isRep?: string;
    customerType?: string;
    loginType?: string;
    warehouse?: string;
    shouldTrackShippingInfo?: boolean;
};

// Define the state type for Google Analytics
export type xGoogleAnalyticsStateType = Readonly<xGoogleAnalyticsHandlerType>;

// Initialize the default state for Google Analytics
const initialXGoogleAnalytics: xGoogleAnalyticsStateType = {
    trackGoogleAnalytics: false,
    // Basic Events
    checkoutFlowEvents: false,
    addToCartEvent: false,
    addToCartEventName: GOOGLE_EVENTS.ADD_TO_CART,
    purchaseEvent: false,
    purchaseEventName: GOOGLE_EVENTS.PURCHASE,
    removeFromCartLineEvent: false,
    removeFromCartLineEventName: GOOGLE_EVENTS.REMOVE_FROM_CART,
    addToWishListEvent: false,
    addToWishListEventName: GOOGLE_EVENTS.ADD_TO_WISHLIST,
    pdpViewEvent: false,
    pdpViewEventName: GOOGLE_EVENTS.VIEW_ITEM,
    productImpressionEvent: false,
    productImpressionEventName: GOOGLE_EVENTS.VIEW_ITEM_LIST,
    selectItemEvent: false,
    selectItemEventName: GOOGLE_EVENTS.SELECT_ITEM,
    viewCartEvent: false,
    viewCartEventName: GOOGLE_EVENTS.VIEW_CART,
    loginEvent: false,
    loginEventName: GOOGLE_EVENTS.LOGIN,
    loginIssueEvent: false,
    loginIssueEventName: GOOGLE_EVENTS.LOGIN_ISSUE,
    searchEvent: false,
    searchEventName: GOOGLE_EVENTS.SEARCH,
    contactUsEvent: false,
    contactUsEventName: GOOGLE_EVENTS.CONTACT_US,
    plpFilterEvent: false,
    plpFilterEventName: GOOGLE_EVENTS.PLP_FILTER,
    plpSearchEvent: false,
    plpSearchEventName: GOOGLE_EVENTS.PLP_SEARCH,
    pdpShareEvent: false,
    pdpShareEventName: GOOGLE_EVENTS.PDP_SHARE,
    requestAQuoteEvent: false,
    requestAQuoteEventName: GOOGLE_EVENTS.REQUEST_A_QUOTE,
    myAcctQuickLinksEvent: false,
    myAcctQuickLinksEventName: GOOGLE_EVENTS.MY_ACCT_QUICKLINKS,
    myAcctFlyoutEvent: false,
    myAcctFlyoutEventName: GOOGLE_EVENTS.MY_ACCT_FLYOUT,
    navBarClickEvent: false,
    navBarClickEventName: GOOGLE_EVENTS.NAV_BAR_CLICK,
    toolBarNavEvent: false,
    toolBarNavEventName: GOOGLE_EVENTS.TOOL_BAR_NAV,
    homepageRibbonevent: false,
    homepageRibbonEventName: GOOGLE_EVENTS.HOMEPAGE_RIBBON,
    requestAccountEvent: false,
    requestAccountEventName: GOOGLE_EVENTS.REQUEST_ACCOUNT,
    locationsEvent: false,
    locationsEventName: GOOGLE_EVENTS.LOCATIONS,
    myacctMylistsEvent: false,
    myacctMylistsEventName: GOOGLE_EVENTS.MYACCT_MYLISTS,
    myacctQuotesEvent: false,
    myacctQuotesEventName: GOOGLE_EVENTS.MYACCT_QUOTES,
    addListToCartEvent: false,
    addListToCartEventName: GOOGLE_EVENTS.ADD_LIST_TO_CART,
    myacctListsMoreactionsEvent: false,
    myacctListsMoreactionsEventName: GOOGLE_EVENTS.MYACCT_LISTS_MOREACTIONS,
    quickOrderSearchEvent: false,
    quickOrderSearchEventName: GOOGLE_EVENTS.QUICK_ORDER_SEARCH,
    quickOrderUploadEvent: false,
    quickOrderUploadEventName: GOOGLE_EVENTS.QUICK_ORDER_UPLOAD,
    requestProductEvent: false,
    requestProductEventName: GOOGLE_EVENTS.REQUEST_PRODUCT,
    editCartEvent: false,
    editCartEventName: GOOGLE_EVENTS.EDIT_CART,
    reorderEvent: false,
    reorderEventName: GOOGLE_EVENTS.REORDER,
    myacctOrderhistoryDaterangeEvent: false,
    myacctOrderhistoryDaterangeEventName: GOOGLE_EVENTS.MYACCT_ORDERHISTORY_DATERANGE,
    myacctOrderhistoryShiptoEvent: false,
    myacctOrderhistoryShiptoEventName: GOOGLE_EVENTS.MYACCT_ORDERHISTORY_SHIPTO,
    myacctOrderEvent: false,
    myacctOrderEventName: GOOGLE_EVENTS.MYACCT_ORDER,
    quoteAcceptedEvent: false,
    quoteAcceptedEventName: GOOGLE_EVENTS.QUOTE_ACCEPTED,
    quoteDeclinedEvent: false,
    quoteDeclinedEventName: GOOGLE_EVENTS.QUOTE_DECLINED,
    quoteProposedEvent: false,
    quoteProposedEventName: GOOGLE_EVENTS.QUOTE_PROPOSED,
    contactSalesEvent: false,
    contactSalesEventName: GOOGLE_EVENTS.CONTACT_SALES,
    pdpImageClickEvent: false,
    pdpImageClickEventName: GOOGLE_EVENTS.PDP_IMAGE_CLICK,
    myacctAddUserEvent: false,
    myacctAddUserEventName: GOOGLE_EVENTS.MYACCT_ADD_USER,
    myacctEditUserEvent: false,
    myacctEditUserEventName: GOOGLE_EVENTS.MYACCT_EDIT_USER,
    myacctViewUserEvent: false,
    myacctViewUserEventName: GOOGLE_EVENTS.MYACCT_VIEW_USER,
    footerClicksEvent: false,
    footerClicksEventName: GOOGLE_EVENTS.FOOTER_CLICKS,
    fileDownloadEvent: false,
    fileDownloadEventName: GOOGLE_EVENTS.FILE_DOWNLOAD,
    pdpWarehouseInventoryEvent: false,
    pdpWarehouseInventoryEventName: GOOGLE_EVENTS.PDP_WAREHOUSE_INVENTORY,
    industriesServedEvent: false,
    industriesServedEventName: GOOGLE_EVENTS.INDUSTRIES_SERVED,
    myacctRecentOrdersEvent: false,
    myacctRecentOrdersEventName: GOOGLE_EVENTS.MYACCT_RECENT_ORDERS,
    // Settings
    includeProductBrand: false,
    includeProductCategory: false,
    // Flags
    shouldTrackPayment: false,
    shouldTrackBeginOnePageCheckout: false,
    affiliation: "",
    billToNum: "",
    shipToNum: "",
    isRep: "",
    customerType: "",
    loginType: "",
    warehouse: "",
    shouldTrackShippingInfo: false,
} as xGoogleAnalyticsStateType;

// Define the reducer to handle Google Analytics actions
const reducer = {
    // Handle updating Google Analytics configuration
    "xGoogleAnalytics/update": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { trackGoogleAnalytics: boolean; data: xGoogleAnalyticsHandlerType },
    ) => {
        draft.trackGoogleAnalytics = action.trackGoogleAnalytics;
        // Basic Events
        draft.checkoutFlowEvents = action.data.checkoutFlowEvents ?? false;
        draft.addToCartEvent = action.data.addToCartEvent ?? false;
        draft.addToCartEventName = action.data.addToCartEventName ?? GOOGLE_EVENTS.ADD_TO_CART;
        draft.purchaseEvent = action.data.purchaseEvent ?? false;
        draft.purchaseEventName = action.data.purchaseEventName ?? GOOGLE_EVENTS.PURCHASE;
        draft.removeFromCartLineEvent = action.data.removeFromCartLineEvent ?? false;
        draft.removeFromCartLineEventName = action.data.removeFromCartLineEventName ?? GOOGLE_EVENTS.REMOVE_FROM_CART;
        draft.addToWishListEvent = action.data.addToWishListEvent ?? false;
        draft.addToWishListEventName = action.data.addToWishListEventName ?? GOOGLE_EVENTS.ADD_TO_WISHLIST;
        draft.pdpViewEvent = action.data.pdpViewEvent ?? false;
        draft.pdpViewEventName = action.data.pdpViewEventName ?? GOOGLE_EVENTS.VIEW_ITEM;
        draft.productImpressionEvent = action.data.productImpressionEvent ?? false;
        draft.productImpressionEventName = action.data.productImpressionEventName ?? GOOGLE_EVENTS.VIEW_ITEM_LIST;
        draft.selectItemEvent = action.data.selectItemEvent ?? false;
        draft.selectItemEventName = action.data.selectItemEventName ?? GOOGLE_EVENTS.SELECT_ITEM;
        draft.viewCartEvent = action.data.viewCartEvent ?? false;
        draft.viewCartEventName = action.data.viewCartEventName ?? GOOGLE_EVENTS.VIEW_CART;
        draft.loginEvent = action.data.loginEvent ?? false;
        draft.loginEventName = action.data.loginEventName ?? GOOGLE_EVENTS.LOGIN;
        draft.searchEvent = action.data.searchEvent ?? false;
        draft.searchEventName = action.data.searchEventName ?? GOOGLE_EVENTS.SEARCH;
        draft.contactUsEvent = action.data.contactUsEvent ?? false;
        draft.contactUsEventName = action.data.contactUsEventName ?? GOOGLE_EVENTS.CONTACT_US;
        draft.plpFilterEvent = action.data.plpFilterEvent ?? false;
        draft.plpFilterEventName = action.data.plpFilterEventName ?? GOOGLE_EVENTS.PLP_FILTER;
        draft.plpSearchEvent = action.data.plpSearchEvent ?? false;
        draft.plpSearchEventName = action.data.plpSearchEventName ?? GOOGLE_EVENTS.PLP_SEARCH;
        draft.pdpShareEvent = action.data.pdpShareEvent ?? false;
        draft.pdpShareEventName = action.data.pdpShareEventName ?? GOOGLE_EVENTS.PDP_SHARE;
        draft.requestAQuoteEvent = action.data.requestAQuoteEvent ?? false;
        draft.requestAQuoteEventName = action.data.requestAQuoteEventName ?? GOOGLE_EVENTS.REQUEST_A_QUOTE;
        draft.myAcctQuickLinksEvent = action.data.myAcctQuickLinksEvent ?? false;
        draft.myAcctQuickLinksEventName = action.data.myAcctQuickLinksEventName ?? GOOGLE_EVENTS.MY_ACCT_QUICKLINKS;
        draft.myAcctFlyoutEvent = action.data.myAcctFlyoutEvent ?? false;
        draft.myAcctFlyoutEventName = action.data.myAcctFlyoutEventName ?? GOOGLE_EVENTS.MY_ACCT_FLYOUT;
        draft.navBarClickEvent = action.data.navBarClickEvent ?? false;
        draft.navBarClickEventName = action.data.navBarClickEventName ?? GOOGLE_EVENTS.NAV_BAR_CLICK;
        draft.toolBarNavEvent = action.data.toolBarNavEvent ?? false;
        draft.toolBarNavEventName = action.data.toolBarNavEventName ?? GOOGLE_EVENTS.TOOL_BAR_NAV;
        draft.homepageRibbonevent = action.data.homepageRibbonevent ?? false;
        draft.homepageRibbonEventName = action.data.homepageRibbonEventName ?? GOOGLE_EVENTS.HOMEPAGE_RIBBON;
        draft.requestAccountEvent = action.data.requestAccountEvent ?? false;
        draft.requestAccountEventName = action.data.requestAccountEventName ?? GOOGLE_EVENTS.REQUEST_ACCOUNT;
        draft.locationsEvent = action.data.locationsEvent ?? false;
        draft.locationsEventName = action.data.locationsEventName ?? GOOGLE_EVENTS.LOCATIONS;
        draft.myacctMylistsEvent = action.data.myacctMylistsEvent ?? false;
        draft.myacctMylistsEventName = action.data.myacctMylistsEventName ?? GOOGLE_EVENTS.MYACCT_MYLISTS;
        draft.myacctQuotesEvent = action.data.myacctQuotesEvent ?? false;
        draft.myacctQuotesEventName = action.data.myacctQuotesEventName ?? GOOGLE_EVENTS.MYACCT_QUOTES;
        draft.addListToCartEvent = action.data.addListToCartEvent ?? false;
        draft.addListToCartEventName = action.data.addListToCartEventName ?? GOOGLE_EVENTS.ADD_LIST_TO_CART;
        draft.myacctListsMoreactionsEvent = action.data.myacctListsMoreactionsEvent ?? false;
        draft.myacctListsMoreactionsEventName =
            action.data.myacctListsMoreactionsEventName ?? GOOGLE_EVENTS.MYACCT_LISTS_MOREACTIONS;
        draft.quickOrderSearchEvent = action.data.quickOrderSearchEvent ?? false;
        draft.quickOrderSearchEventName = action.data.quickOrderSearchEventName ?? GOOGLE_EVENTS.QUICK_ORDER_SEARCH;
        draft.quickOrderUploadEvent = action.data.quickOrderUploadEvent ?? false;
        draft.quickOrderUploadEventName = action.data.quickOrderUploadEventName ?? GOOGLE_EVENTS.QUICK_ORDER_UPLOAD;
        draft.requestProductEvent = action.data.requestProductEvent ?? false;
        draft.requestProductEventName = action.data.requestProductEventName ?? GOOGLE_EVENTS.REQUEST_PRODUCT;
        draft.editCartEvent = action.data.editCartEvent ?? false;
        draft.editCartEventName = action.data.editCartEventName ?? GOOGLE_EVENTS.EDIT_CART;
        draft.reorderEvent = action.data.reorderEvent ?? false;
        draft.reorderEventName = action.data.reorderEventName ?? GOOGLE_EVENTS.REORDER;
        draft.myacctOrderhistoryDaterangeEvent = action.data.myacctOrderhistoryDaterangeEvent ?? false;
        draft.myacctOrderhistoryDaterangeEventName = action.data.myacctOrderhistoryDaterangeEventName ?? GOOGLE_EVENTS.MYACCT_ORDERHISTORY_DATERANGE;
        draft.myacctOrderhistoryShiptoEvent = action.data.myacctOrderhistoryShiptoEvent ?? false;
        draft.myacctOrderhistoryShiptoEventName = action.data.myacctOrderhistoryShiptoEventName ?? GOOGLE_EVENTS.MYACCT_ORDERHISTORY_SHIPTO;
        draft.myacctOrderEvent = action.data.myacctOrderEvent ?? false;
        draft.myacctOrderEventName = action.data.myacctOrderEventName ?? GOOGLE_EVENTS.MYACCT_ORDER;
        draft.quoteAcceptedEvent = action.data.quoteAcceptedEvent ?? false;
        draft.quoteAcceptedEventName = action.data.quoteAcceptedEventName ?? GOOGLE_EVENTS.QUOTE_ACCEPTED;
        draft.quoteDeclinedEvent = action.data.quoteDeclinedEvent ?? false;
        draft.quoteDeclinedEventName = action.data.quoteDeclinedEventName ?? GOOGLE_EVENTS.QUOTE_DECLINED;
        draft.quoteProposedEvent = action.data.quoteProposedEvent ?? false;
        draft.quoteProposedEventName = action.data.quoteProposedEventName ?? GOOGLE_EVENTS.QUOTE_PROPOSED;
        draft.contactSalesEvent = action.data.contactSalesEvent ?? false;
        draft.contactSalesEventName = action.data.contactSalesEventName ?? GOOGLE_EVENTS.CONTACT_SALES;
        draft.pdpImageClickEvent = action.data.pdpImageClickEvent ?? false;
        draft.pdpImageClickEventName = action.data.pdpImageClickEventName ?? GOOGLE_EVENTS.PDP_IMAGE_CLICK;
        draft.myacctAddUserEvent = action.data.myacctAddUserEvent ?? false;
        draft.myacctAddUserEventName = action.data.myacctAddUserEventName ?? GOOGLE_EVENTS.MYACCT_ADD_USER;
        draft.myacctEditUserEvent = action.data.myacctEditUserEvent ?? false;
        draft.myacctEditUserEventName = action.data.myacctEditUserEventName ?? GOOGLE_EVENTS.MYACCT_EDIT_USER;
        draft.myacctViewUserEvent = action.data.myacctViewUserEvent ?? false;
        draft.myacctViewUserEventName = action.data.myacctViewUserEventName ?? GOOGLE_EVENTS.MYACCT_VIEW_USER;
        draft.footerClicksEvent = action.data.footerClicksEvent ?? false;
        draft.footerClicksEventName = action.data.footerClicksEventName ?? GOOGLE_EVENTS.FOOTER_CLICKS;
        draft.fileDownloadEvent = action.data.fileDownloadEvent ?? false;
        draft.fileDownloadEventName = action.data.fileDownloadEventName ?? GOOGLE_EVENTS.FILE_DOWNLOAD;
        draft.pdpWarehouseInventoryEvent = action.data.pdpWarehouseInventoryEvent ?? false;
        draft.pdpWarehouseInventoryEventName = action.data.pdpWarehouseInventoryEventName ?? GOOGLE_EVENTS.PDP_WAREHOUSE_INVENTORY;
        draft.loginIssueEvent = action.data.loginIssueEvent ?? false;
        draft.loginIssueEventName = action.data.loginIssueEventName ?? GOOGLE_EVENTS.LOGIN_ISSUE;
        draft.industriesServedEvent = action.data.industriesServedEvent ?? false;
        draft.industriesServedEventName = action.data.industriesServedEventName ?? GOOGLE_EVENTS.INDUSTRIES_SERVED;
        draft.myacctRecentOrdersEvent = action.data.myacctRecentOrdersEvent ?? false;
        draft.myacctRecentOrdersEventName = action.data.myacctRecentOrdersEventName ?? GOOGLE_EVENTS.MYACCT_RECENT_ORDERS;
        // Settings
        draft.includeProductBrand = action.data.includeProductBrand ?? false;
        draft.includeProductCategory = action.data.includeProductCategory ?? false;
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackPayment": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { shouldTrackPayment: boolean },
    ) => {
        draft.shouldTrackPayment = action.shouldTrackPayment;
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { shouldTrackBeginOnePageCheckout: boolean },
    ) => {
        draft.shouldTrackBeginOnePageCheckout = action.shouldTrackBeginOnePageCheckout;
    },
    // Handle setting the affiliation
    "xGoogleAnalytics/setAffiliation": (draft: Draft<xGoogleAnalyticsStateType>, action: { affiliation: string }) => {
        draft.affiliation = action.affiliation;
    },
    // Handle setting the billToNum and shipToNum
    "xGoogleAnalytics/setCustomerNumbers": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { billToNum?: string; shipToNum?: string },
    ) => {
        if (action.billToNum !== undefined) {
            draft.billToNum = action.billToNum;
        }
        if (action.shipToNum !== undefined) {
            draft.shipToNum = action.shipToNum;
        }
    },
    // Handle setting the salesRep
    "xGoogleAnalytics/setSalesRep": (draft: Draft<xGoogleAnalyticsStateType>, action: { salesRep?: string }) => {
        draft.salesRep = action.salesRep;
    },
    // Handle setting the shipping info tracking flag
    "xGoogleAnalytics/setShippingInfoEventTrackingFlags": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { shouldTrackShippingInfo: boolean; triggerDefaultShippingInfo?: boolean },
    ) => {
        draft.shouldTrackShippingInfo = action.shouldTrackShippingInfo;
        action.triggerDefaultShippingInfo !== undefined &&
            setTriggerDefaultShippingInfo(action.triggerDefaultShippingInfo);
    },
    // Handle setting the isRep
    "xGoogleAnalytics/setIsRep": (draft: Draft<xGoogleAnalyticsStateType>, action: { isRep?: string }) => {
        draft.isRep = action.isRep;
    },
    // Handle setting the customerType
    "xGoogleAnalytics/setCustomerType": (draft: Draft<xGoogleAnalyticsStateType>, action: { customerType?: string }) => {
        draft.customerType = action.customerType;
    },
    // Handle setting the loginType
    "xGoogleAnalytics/setLoginType": (draft: Draft<xGoogleAnalyticsStateType>, action: { loginType?: string }) => {
        draft.loginType = action.loginType;
    },
    // Handle setting the warehouse
    "xGoogleAnalytics/setWarehouse": (draft: Draft<xGoogleAnalyticsStateType>, action: { warehouse?: string }) => {
        draft.warehouse = action.warehouse;
    },
};

export const setTriggerDefaultShippingInfo = (triggerDefaultShippingInfo?: boolean) => {
    window.sessionStorage.setItem("triggerDefaultShippingInfo", triggerDefaultShippingInfo?.toString() || "");
};

// Create the Google Analytics reducer using Immer for immutability
const XGoogleAnalytics = createTypedReducerWithImmer(initialXGoogleAnalytics, reducer);

// Add the Google Analytics reducer to the application's reducers
(reducers as any).xGoogleAnalytics = XGoogleAnalytics;

// Define the application state including Google Analytics state
export type xGoogleAnalyticsState = {
    xGoogleAnalytics: xGoogleAnalyticsStateType;
} & ApplicationState;

// Define the type for Google Analytics reducers
type xGoogleAnalyticsReducers = {
    [key: string]: typeof XGoogleAnalytics;
};

// Define the type for Google Analytics actions
export type xGoogleAnalyticsActions =
    | Parameters<xGoogleAnalyticsReducers[keyof xGoogleAnalyticsReducers]>[1]
    | AnyAction;
